"use client";

import { useEffect, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { Calendar, Check, Globe, type LucideIcon } from "lucide-react";

import {
  DEFAULT_SELECTED_CHANNELS,
  EMR_PLATFORMS,
  SCHEDULING_SUBTYPES,
} from "~/lib/siteglue/business-ready/channel-catalog";
import type { AgentId, ChannelId, EmrPlatform, SchedulingSubtype } from "~/lib/siteglue/business-ready/types";
import { scannerOpeningHref, scannerReportHref } from "~/lib/siteglue/agent-ready-host";
import {
  persistLandingUrl,
  resolveScannerPrefillUrl,
  normalizeBusinessWebsite,
  URL_HELPER_INVALID,
} from "~/lib/siteglue/landing-redesign/url";
import {
  SCAN_EMR_LABEL,
  SCAN_NOW,
  SCAN_REPORTS_LABEL,
  SCAN_SCHEDULE_LABEL,
  SCAN_TOOLS_HELP,
  SCAN_TOOLS_LABEL,
  SCAN_WIZARD_BACK,
  SCAN_WIZARD_CONTINUE,
  SCAN_WIZARD_NEED_AGENT,
  SCAN_WIZARD_NEED_CHANNEL,
  SCAN_WIZARD_NEED_EMR,
  SCAN_WIZARD_NEED_REPORT,
  SCAN_WIZARD_NEED_SCHEDULE,
  SCAN_WIZARD_STEP_REPORTS,
  SCAN_WIZARD_STEP_TOOLS,
  SCAN_WIZARD_STEP_WEBSITE,
  URL_LABEL,
  URL_PLACEHOLDER,
} from "~/lib/siteglue/landing-redesign/copy";
import {
  isAgentTestSelected,
  isCroSelected,
  selectionForReports,
  type ScanReportKind,
} from "~/lib/siteglue/agent-readiness/selection";
import { DEFAULT_AUDIT_REPORTS, ScanAuditCards } from "~/components/siteglue/agent-ready/scan-audit-cards";
import { domainFromUrl } from "~/lib/siteglue/scan/funnel";
import { trackAgentReadyEvent } from "~/lib/siteglue/scan/funnel-client";
import { cn } from "~/lib/utils";

const DEFAULT_REPORTS: ScanReportKind[] = DEFAULT_AUDIT_REPORTS;
const STEPS = [SCAN_WIZARD_STEP_WEBSITE, SCAN_WIZARD_STEP_REPORTS, SCAN_WIZARD_STEP_TOOLS] as const;
const LAST_STEP = STEPS.length - 1;

function ChoiceTile({
  checked,
  disabled,
  title,
  subtitle,
  icon: Icon,
  plain = false,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  plain?: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 px-4 py-3.5 transition",
        plain
          ? ""
          : checked
            ? "rounded-2xl border border-[#4f81bd] bg-[#4f81bd]/[0.06] shadow-[0_8px_20px_rgba(79,129,189,0.08)]"
            : "rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
        disabled && "cursor-default",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="sr-only"
      />
      {Icon ? (
        <span
          className={cn(
            "mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
            checked ? "bg-[#4f81bd]/10 text-[#4f81bd]" : "bg-slate-100 text-slate-500",
          )}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-900">{title}</span>
        {subtitle ? (
          <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{subtitle}</span>
        ) : null}
      </span>
      <span
        className={cn(
          "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
          checked ? "border-[#4f81bd] bg-[#4f81bd] text-white" : "border-slate-300 bg-white",
        )}
        aria-hidden
      >
        {checked ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
      </span>
    </label>
  );
}

function toggleValue<T extends string>(current: T[], id: T, locked?: T): T[] {
  if (locked && id === locked) return current.includes(id) ? current : [...current, id];
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}

export function AgentReadyScanForm({ scannerHost = false }: { scannerHost?: boolean }) {
  useEffect(() => {
    trackAgentReadyEvent("agentready_page_view", { domain: window.location.hostname });
  }, []);

  return <AgentReadyUrlForm scannerHost={scannerHost} />;
}

export function AgentReadyUrlForm({
  scannerHost = false,
  className,
  initialUrl = "",
  urlPlaceholder = URL_PLACEHOLDER,
  stepZeroCta,
  onStepChange,
  hideSteps = false,
  urlOnly = false,
  fixedReports,
}: {
  scannerHost?: boolean;
  className?: string;
  initialUrl?: string;
  urlPlaceholder?: string;
  stepZeroCta?: string;
  onStepChange?: (step: number) => void;
  hideSteps?: boolean;
  /** Homepage and dedicated audit CTAs: URL then scan, no channel/agent/task steps. */
  urlOnly?: boolean;
  fixedReports?: ScanReportKind[];
}) {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [url, setUrl] = useState(() => resolveScannerPrefillUrl(initialUrl));
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [reports, setReports] = useState<ScanReportKind[]>(fixedReports?.length ? fixedReports : DEFAULT_REPORTS);
  const [agents, setAgents] = useState<AgentId[]>([]);
  const [walkTasks, setWalkTasks] = useState<string[]>([]);
  const [channels, setChannels] = useState<ChannelId[]>(DEFAULT_SELECTED_CHANNELS);
  const [appointmentsEnabled, setAppointmentsEnabled] = useState(false);
  const [schedulingSubtypes, setSchedulingSubtypes] = useState<SchedulingSubtype[]>([]);
  const [emrPlatforms, setEmrPlatforms] = useState<EmrPlatform[]>([]);

  useEffect(() => {
    const fromQuery =
      typeof window === "undefined"
        ? ""
        : resolveScannerPrefillUrl(new URLSearchParams(window.location.search).get("url"));
    const next = resolveScannerPrefillUrl(initialUrl) || fromQuery;
    if (!next) return;
    setUrl(next);
    persistLandingUrl(next);
  }, [initialUrl]);

  useEffect(() => {
    onStepChange?.(step);
  }, [onStepChange, step]);

  const scanChannels = (): ChannelId[] => {
    const next = channels.filter((channel) => channel !== "scheduling");
    return appointmentsEnabled ? [...next, "scheduling"] : next;
  };

  const startScan = async () => {
    const normalized = normalizeBusinessWebsite(url);
    if (!normalized.ok) {
      setError(normalized.reason === "empty" ? "Enter a website address." : URL_HELPER_INVALID);
      setStep(0);
      return;
    }
    const trimmed = normalized.href;
    if (reports.length === 0) {
      setError(SCAN_WIZARD_NEED_REPORT);
      setStep(1);
      return;
    }
    if (isAgentTestSelected(reports) && agents.length === 0) {
      setError(SCAN_WIZARD_NEED_AGENT);
      setStep(1);
      return;
    }
    if (isCroSelected(reports) && scanChannels().length === 0) {
      setError(SCAN_WIZARD_NEED_CHANNEL);
      setStep(1);
      return;
    }
    if (appointmentsEnabled && schedulingSubtypes.length === 0) {
      setError(SCAN_WIZARD_NEED_SCHEDULE);
      setStep(2);
      return;
    }
    if (appointmentsEnabled && schedulingSubtypes.includes("emr") && emrPlatforms.length === 0) {
      setError(SCAN_WIZARD_NEED_EMR);
      setStep(2);
      return;
    }
    setPending(true);
    setError("");
    const reportTab = window.open(scannerOpeningHref(scannerHost), "_blank");
    try {
      const response = await fetch("/api/siteglue/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: trimmed,
          reports,
          selection: selectionForReports(reports),
          skipped: false,
          agents: isAgentTestSelected(reports) ? agents : [],
          walkTasks: isAgentTestSelected(reports) ? walkTasks : [],
          channels: isCroSelected(reports) ? scanChannels() : ["website"],
          schedulingSubtypes: appointmentsEnabled ? schedulingSubtypes : [],
          emrPlatforms: appointmentsEnabled && schedulingSubtypes.includes("emr") ? emrPlatforms : [],
        }),
      });
      const data = (await response.json().catch(() => null)) as { id?: string; error?: string } | null;
      if (!response.ok || !data?.id) {
        reportTab?.close();
        setError(data?.error || "The scan could not start.");
        setPending(false);
        return;
      }
      trackAgentReadyEvent("agentready_scan_started", {
        scanId: data.id,
        domain: domainFromUrl(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`),
        preset: selectionForReports(reports).preset,
      });
      const href = scannerReportHref(data.id, scannerHost);
      if (reportTab) reportTab.location.href = href;
      else router.push(href);
      setPending(false);
    } catch {
      reportTab?.close();
      setError("The scan could not start.");
      setPending(false);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (step === 0) {
      const normalized = normalizeBusinessWebsite(url);
      if (!normalized.ok) {
        setError(normalized.reason === "empty" ? "Enter a website address." : URL_HELPER_INVALID);
        return;
      }
      setUrl(normalized.href);
      persistLandingUrl(normalized.href);
      setError("");
      if (urlOnly) {
        await startScan();
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (reports.length === 0) {
        setError(SCAN_WIZARD_NEED_REPORT);
        return;
      }
      if (isAgentTestSelected(reports) && agents.length === 0) {
        setError(SCAN_WIZARD_NEED_AGENT);
        return;
      }
      if (isCroSelected(reports) && scanChannels().length === 0) {
        setError(SCAN_WIZARD_NEED_CHANNEL);
        return;
      }
      setError("");
      setStep(2);
      return;
    }
    if (appointmentsEnabled && schedulingSubtypes.length === 0) {
      setError(SCAN_WIZARD_NEED_SCHEDULE);
      return;
    }
    if (appointmentsEnabled && schedulingSubtypes.includes("emr") && emrPlatforms.length === 0) {
      setError(SCAN_WIZARD_NEED_EMR);
      return;
    }
    await startScan();
  };

  return (
    <form onSubmit={(event) => void onSubmit(event)} className={cn("mx-auto mt-6 w-full max-w-2xl text-left", className)}>
      {hideSteps ? null : (
        <ol className="mb-4 flex items-center justify-center gap-2" aria-label="Scan steps">
          {STEPS.map((label, index) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-semibold ${
                  index === step ? "bg-slate-900 text-white" : index < step ? "bg-[#4f81bd] text-white" : "bg-slate-100 text-slate-500"
                }`}
              >
                {index + 1}
              </span>
              <span className={`hidden text-xs font-semibold sm:inline ${index === step ? "text-slate-900" : "text-slate-500"}`}>
                {label}
              </span>
              {index < STEPS.length - 1 ? <span className="h-px w-6 bg-slate-200 sm:w-10" aria-hidden /> : null}
            </li>
          ))}
        </ol>
      )}

      {step === 0 ? (
        <ScanUrlBar url={url} onUrlChange={setUrl} placeholder={urlPlaceholder} submitLabel={stepZeroCta} />
      ) : (
        <div className="rounded-[1.6rem] border border-slate-200 bg-white p-5 shadow-[0_10px_40px_rgba(15,23,42,0.06)]">
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Globe className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
            <span className="truncate font-medium text-slate-900">{url}</span>
          </p>
          {step === 1 ? (
            <fieldset className="mt-4">
              <legend className="text-sm font-semibold text-slate-900">{SCAN_REPORTS_LABEL}</legend>
              <div className="mt-4">
                <ScanAuditCards
                  reports={reports}
                  onReportsChange={setReports}
                  agents={agents}
                  onAgentsChange={setAgents}
                  channels={channels}
                  onChannelsChange={setChannels}
                  walkTasks={walkTasks}
                  onWalkTasksChange={setWalkTasks}
                />
              </div>
            </fieldset>
          ) : (
            <fieldset className="mt-4">
              <legend className="sr-only">{SCAN_TOOLS_LABEL}</legend>
              <ChoiceTile
                checked={appointmentsEnabled}
                title={SCAN_TOOLS_HELP}
                icon={Calendar}
                onChange={() => {
                  setAppointmentsEnabled((current) => {
                    const next = !current;
                    if (!next) {
                      setSchedulingSubtypes([]);
                      setEmrPlatforms([]);
                    }
                    return next;
                  });
                }}
              />
              {appointmentsEnabled ? (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-slate-900">{SCAN_SCHEDULE_LABEL}</p>
                  <p className="mt-1 text-sm text-slate-600">{SCAN_WIZARD_NEED_SCHEDULE}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SCHEDULING_SUBTYPES.map((item) => {
                      const checked = schedulingSubtypes.includes(item.id);
                      return (
                        <label
                          key={item.id}
                          className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                            checked ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setSchedulingSubtypes((current) => {
                                const next = toggleValue(current, item.id);
                                if (!next.includes("emr")) setEmrPlatforms([]);
                                return next;
                              })
                            }
                            className="sr-only"
                          />
                          {item.label}
                        </label>
                      );
                    })}
                  </div>
                  {schedulingSubtypes.includes("emr") ? (
                    <div className="mt-4">
                      <p className="text-sm font-semibold text-slate-900">{SCAN_EMR_LABEL}</p>
                      <p className="mt-1 text-sm text-slate-600">{SCAN_WIZARD_NEED_EMR}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {EMR_PLATFORMS.map((item) => {
                          const checked = emrPlatforms.includes(item.id);
                          return (
                            <label
                              key={item.id}
                              className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                                checked ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setEmrPlatforms((current) => toggleValue(current, item.id))}
                                className="sr-only"
                              />
                              {item.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </fieldset>
          )}
        </div>
      )}

      <div className={`mt-4 flex ${step === 0 ? "justify-center" : "justify-between"} gap-2`}>
        {step > 0 ? (
          <button
            type="button"
            onClick={() => {
              setError("");
              setStep((current) => current - 1);
            }}
            className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800"
          >
            {SCAN_WIZARD_BACK}
          </button>
        ) : null}
        {step > 0 || !stepZeroCta ? (
          <button
            type="submit"
            disabled={pending}
            className="rounded-full bg-[#4f81bd] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {pending ? "Scanning…" : step < LAST_STEP ? SCAN_WIZARD_CONTINUE : SCAN_NOW}
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="mt-3 text-center text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function ScanUrlBar({
  url,
  onUrlChange,
  placeholder = URL_PLACEHOLDER,
  submitLabel,
}: {
  url: string;
  onUrlChange: (value: string) => void;
  placeholder?: string;
  submitLabel?: string;
}) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-2 rounded-[1.6rem] border border-slate-200 bg-white p-1.5 shadow-[0_10px_40px_rgba(15,23,42,0.06)] sm:flex-row sm:items-center sm:rounded-full">
      <label className="flex min-w-0 flex-1 items-center gap-2 px-3">
        <Globe className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <input
          name="url"
          type="text"
          inputMode="url"
          autoComplete="url"
          required={false}
          aria-label={URL_LABEL}
          placeholder={placeholder}
          value={url}
          onChange={(event) => onUrlChange(event.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent py-2.5 text-base text-slate-900 outline-none placeholder:text-slate-400"
        />
      </label>
      {submitLabel ? (
        <button
          type="submit"
          className="inline-flex h-12 items-center justify-center rounded-xl bg-[#4f81bd] px-5 text-sm font-semibold text-white hover:opacity-95 sm:h-11 sm:rounded-full"
        >
          {submitLabel}
        </button>
      ) : null}
    </div>
  );
}
